import { ApplicationSelectData, ApplicationsMetaData, ApplicationType } from '@sec-loans-ui/common/types'
import { RootStepType } from '@sec-loans-ui/constants'
import { useSendSecLoansPageViewEvent } from '@sec-loans-ui/hooks/useSendSecLoansPageViewEvent'
import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { User } from '@sec-loans-ui/wizard/types'
import { getOpenErrorPromptModalEvent } from '@ui-common/components/Modal'
import { GlobalServicesContext } from '@ui-common/state'
import { getFullname } from '@ui-common/utils'
import { PlusIcon } from '@ui-components/assets/Icons'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { FormSection } from '@ui-components/global/FormStyling'
import { CatalogStep } from '@ui-components/layout/CatalogStep'
import { use } from 'react'
import { useIntl } from 'react-intl'
import * as R from 'remeda'

import { ExistingApplicationsView } from './components/ExistingApplicationsView'
import { NoExistingApplicationsView } from './components/NoExistingApplicationsView'
import { getApplicationDetails } from './loanCatalogStepUtils'

export interface LoanCatalogStepProps {
  onApplicationSelect: (input: ApplicationSelectData) => void
  onNewApplicationSelect: () => void
  onAbort: () => unknown
  user: User
  applications: ApplicationsMetaData
}

export const LoanCatalogStep = ({
  onApplicationSelect,
  onNewApplicationSelect,
  onAbort,
  user,
  applications,
}: LoanCatalogStepProps) => {
  useSendSecLoansPageViewEvent(RootStepType.Catalog)
  const { analyticsService } = useAnalyticsService()
  const intl = useIntl()
  const { modalService } = use(GlobalServicesContext)
  const { inProgressApplications, completedApplications } = getApplicationDetails(applications)

  const hasExistingApplications = inProgressApplications.length > 0 || completedApplications.length > 0

  const handleApplicationSelect = (input: ApplicationSelectData) => {
    if (input.applicationType === ApplicationType.LoanApplication) {
      analyticsService.sendStepCustomEvent(RootStepType.Catalog, `SelectLoanApplication`)
      if (!user.address || (!user.address.domesticAddress && !user.address.foreignAddress)) {
        modalService.send(
          getOpenErrorPromptModalEvent(
            R.doNothing(),
            intl,
            intl.formatMessage({ id: 'sec-loans-application-select-error-modal-no-address-header' }),
            intl.formatMessage({ id: 'sec-loans-application-select-error-modal-no-address-description' }),
            ModalVisualMode.Banned,
          ),
        )
        return
      }
    } else if (input.applicationType === ApplicationType.InstalmentFreePeriodApplication) {
      analyticsService.sendStepCustomEvent(RootStepType.Catalog, 'SelectIfpApplication')
    } else if (input.applicationType === ApplicationType.LoanOffer) {
      analyticsService.sendStepCustomEvent(RootStepType.Catalog, 'SelectLoanOffer')
    }
    onApplicationSelect(input)
  }

  return (
    <CatalogStep
      actionBarOptions={{
        label: intl.formatMessage({ id: 'application-list-new-application' }),
        icon: <PlusIcon />,
        onClick: onNewApplicationSelect,
      }}
      actionButtonOptions={{
        label: intl.formatMessage({ id: 'common-exit' }),
        onClick: onAbort,
      }}
      displayName={getFullname({ firstName: user.firstNames, lastName: user.lastName })}
      header={intl.formatMessage({ id: 'application-list-header' })}
      data-testid={RootStepType.Catalog}
    >
      <FormSection>
        {hasExistingApplications ? (
          <ExistingApplicationsView
            debtorUuid={user.debtorUuid}
            inProgressApplications={inProgressApplications}
            completedApplications={completedApplications}
            onApplicationSelect={handleApplicationSelect}
          />
        ) : (
          <NoExistingApplicationsView onNewApplicationSelect={onNewApplicationSelect} />
        )}
      </FormSection>
    </CatalogStep>
  )
}
